'use client';

/**
 * PresetSwitcher — chip strip on the canvas chrome.
 *
 * Renders one chip per entry in the active selection's
 * `available_presets`. Clicking flips `usePresetStore` for the
 * current `(mode, selection)`; `<CardsCanvasWidget />` re-reads the
 * active preset and paints from its `canvas_cards`.
 *
 * Renders nothing when `available_presets` is empty (single-preset
 * selections — no switcher chrome). The first entry is always the
 * default; selecting any chip is purely visual until the user clicks.
 *
 * @see klyde_edge/ui_config/total_matrix.py — PresetOption
 * @see klyde_ipe/lib/stores/preset-store.ts
 *
 * @layer presentation
 * @arch_component_type chrome
 * @arch_domain canvas
 */

import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useTotalMatrixStore, useAffordance } from '@/lib/stores/total-matrix-store';
import { usePresetStore, useActivePresetKey } from '@/lib/stores/preset-store';
import { cn } from '@/lib/utils/cn';

function PresetChip({
  opt, isActive, onClick,
}: {
  opt: { key: string; label: string }; isActive: boolean; onClick: () => void;
}) {
  // Dynamic — every available preset renders as a simultaneous sibling
  // chip, so it's addressed by the preset's own key (params={key}),
  // not a static one. One @affordance covers every preset.
  const testId = useAffordance('canvas.preset_switcher.tab', { key: opt.key });
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
        isActive
          ? 'bg-background text-foreground shadow-sm border border-border'
          : 'text-muted-foreground hover:bg-accent/50',
      )}
    >
      {opt.label}
    </button>
  );
}

export function PresetSwitcher() {
  // Keys off `strategySelection` — same axis the canvas widget uses,
  // so graph-internal clicks (VR_ / line-item / vendor) don't reset
  // which preset chip is active. Active-preset state is per-
  // (mode, strategySelection) in `usePresetStore`; the chip strip
  // and the canvas paint must agree on the lookup key.
  const { mode, strategySelection: selection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const setActive = usePresetStore((s) => s.setActive);
  const activeKey = useActivePresetKey(mode, selection);

  const cfg = getViewportConfig(mode, selection);
  const options = cfg?.available_presets ?? [];

  if (options.length === 0) return null;

  // Default = first entry. When no user override exists, the chip
  // strip highlights the default chip.
  const effectiveKey = activeKey ?? options[0]?.key;

  return (
    <div className="flex items-center gap-1 px-3 py-1.5 border-b border-border bg-muted/20">
      {options.map((opt) => (
        <PresetChip
          key={opt.key}
          opt={opt}
          isActive={opt.key === effectiveKey}
          onClick={() => setActive(mode, selection, opt.key)}
        />
      ))}
    </div>
  );
}
